import React from 'react';
import { observer } from 'mobx-react';
import { Input, Select, Spin, Switch } from 'antd';
import { useFormik } from 'formik';
import { useLoyaltyClubStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import {
    APP_TARGET_CONFIG,
    APP_TARGETS,
    LoyaltyClub,
    LoyaltyClubUpdatePayload,
    RewardOptionCoupon,
    TierLadderMilestonePayload,
} from '../../stores/loyalty-club';
import {
    accumulateTierThresholds,
    buildCouponGroups,
    collapseToOptions,
    CouponGroup,
    MilestoneForm,
    MilestonesSection,
} from './components/MilestonesSection';
import { Lang } from '../../utils';
import { Brand, DeepLinkSection } from '../../components/deep-link-section';
import { BackgroundColorPicker } from '../../components/background-color-picker';
import { ImageInput } from '../../components/image-input';
import { LabeledField } from '../../components/form-fields/labeled-field';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { SaveButton } from '../../components/action-buttons';
import { Section } from '../../components/form-fields/section';
import { TimezoneSelect } from '../../components/timezone-select';
import { DatePicker } from '../../components/date-picker';
import {
    convertLocalToUtcString,
    convertUtcToLocalString,
    DATE_TIME_FORMAT,
    DEFAULT_TIMEZONE,
} from '../../utils/timezone';
import style from './style.module.scss';

type TranslationFormValues = {
    language: Lang;
    name: string;
    subtitle: string | null;
    description: string | null;
    // Couche-Tard art. `ckImage` holds the Circle K variant; the API resolves
    // which one each app sees from its app target, so both are set here.
    // The names are asymmetric on purpose (RNGR-2426): Couche-Tard keeps the
    // unprefixed one so already-released apps go on reading it unchanged,
    // matching deepLink / ckDeepLink on the same club.
    image: string | null;
    ckImage: string | null;
    backgroundColor: string | null;
};

type ConfigFormValues = {
    appTarget: string;
    timezone: string;
    // Local wall-clock strings in `timezone`; converted to UTC on save.
    startDate: string;
    endDate: string;
    graceEndDate: string;
    isActive: boolean;
    translations: TranslationFormValues[];
};

const EMPTY_FORM: ConfigFormValues = {
    appTarget: 'All',
    timezone: DEFAULT_TIMEZONE,
    startDate: '',
    endDate: '',
    graceEndDate: '',
    isActive: false,
    translations: [],
};

/**
 * The stored ladder as the form holds it. `requiredPurchases` is a per-tier
 * delta; a human authors absolute visit counts, so accumulate on the way in and
 * difference on the way out.
 */
function toMilestoneForms(
    club: LoyaltyClub,
    groups: CouponGroup[]
): MilestoneForm[] {
    return accumulateTierThresholds(club.tierRewards).map(
        ({ tier, visits }) => ({
            id: `tier-${tier.id}`,
            visits,
            options: collapseToOptions(
                (tier.rewardOptions ?? []).map((option) => option.promotionId),
                groups
            ),
        })
    );
}

/**
 * One picked coupon expands back into one option row per region, which is what
 * the ladder endpoint stores. The grouping exists so a human picks once; the
 * contract underneath is unchanged.
 */
function toLadderPayload(
    milestones: MilestoneForm[],
    groupsByKey: Map<string, CouponGroup>
): TierLadderMilestonePayload[] {
    return milestones.map((milestone) => ({
        visits: milestone.visits as number,
        options: milestone.options.flatMap((option) => {
            const group = option.groupKey
                ? groupsByKey.get(option.groupKey)
                : undefined;

            return (group?.promotionIds ?? []).map((promotionId) => ({
                promotionId,
            }));
        }),
    }));
}

/**
 * The client-side half of "a milestone needs at least one coupon". The server
 * enforces the same rule; this exists so the message lands next to the offending
 * milestone instead of arriving as a failed request.
 */
function validateMilestones(
    milestones: MilestoneForm[]
): Record<string, string> {
    const errors: Record<string, string> = {};
    let previous = 0;

    // A milestone shows one message, and the first fault found is the one that
    // survives. Assigning unconditionally would let the coupon check overwrite
    // the visits message, so a milestone missing both only ever reports one.
    const fail = (milestoneId: string, message: string) => {
        if (!(milestoneId in errors)) {
            errors[milestoneId] = message;
        }
    };

    for (const milestone of milestones) {
        if (milestone.visits === null || milestone.visits < 1) {
            fail(milestone.id, 'Set the number of visits (1 or more).');
        } else if (milestone.visits <= previous) {
            fail(
                milestone.id,
                `Visits must increase down the ladder (${milestone.visits} follows ${previous}).`
            );
        }

        if (milestone.visits !== null) {
            previous = milestone.visits;
        }

        const chosen = milestone.options.filter(
            (option) => option.groupKey !== null
        );
        if (chosen.length === 0) {
            fail(milestone.id, 'Add at least one coupon to this milestone.');
        } else if (
            new Set(chosen.map((option) => option.groupKey)).size !==
            chosen.length
        ) {
            fail(milestone.id, 'This milestone offers the same coupon twice.');
        }
    }

    return errors;
}

function toFormValues(club: LoyaltyClub): ConfigFormValues {
    const timezone = club.timezone || DEFAULT_TIMEZONE;

    return {
        appTarget: club.appTarget ?? 'All',
        timezone,
        startDate: convertUtcToLocalString(club.startDate, timezone) ?? '',
        endDate: convertUtcToLocalString(club.endDate, timezone) ?? '',
        graceEndDate:
            convertUtcToLocalString(club.graceEndDate, timezone) ?? '',
        isActive: club.active,
        translations: Lang.map((language) => {
            const existing = club.translations.find(
                (t) => t.language === language
            );
            return {
                language,
                name: existing?.name ?? '',
                subtitle: existing?.subtitle ?? null,
                description: existing?.description ?? null,
                image: existing?.image ?? null,
                ckImage: existing?.ckImage ?? null,
                backgroundColor: existing?.backgroundColor ?? null,
            };
        }),
    };
}

export const RoadToRewardsConfigurationPage = observer(() => {
    const loyaltyClubStore = useLoyaltyClubStore();

    const [club, setClub] = React.useState<LoyaltyClub | null>(null);
    const [isFetching, setIsFetching] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [milestones, setMilestones] = React.useState<MilestoneForm[]>([]);
    const [milestonesDirty, setMilestonesDirty] = React.useState(false);
    const [milestoneErrors, setMilestoneErrors] = React.useState<
        Record<string, string>
    >({});
    const [coupons, setCoupons] = React.useState<RewardOptionCoupon[]>([]);

    const couponGroups = React.useMemo(() => buildCouponGroups(coupons), [
        coupons,
    ]);
    const groupsByKey = React.useMemo(
        () => new Map(couponGroups.map((group) => [group.key, group])),
        [couponGroups]
    );

    const onMilestonesChange = (next: MilestoneForm[]) => {
        setMilestones(next);
        setMilestonesDirty(true);
        setMilestoneErrors({});
    };

    const formik = useFormik<ConfigFormValues>({
        initialValues: EMPTY_FORM,
        enableReinitialize: false,
        onSubmit: async () => undefined,
    });

    /** Whether the configuration actually reached the server. */
    const saveConfiguration = async (): Promise<boolean> => {
        if (!club) return false;

        const errors = validateMilestones(milestones);
        if (Object.keys(errors).length > 0) {
            setMilestoneErrors(errors);
            NotificationManager.showError(
                'Fix the highlighted milestones before saving'
            );
            return false;
        }

        const { timezone } = formik.values;

        // registrationStartDate / registrationEndDate are not on this screen but
        // the club PUT requires them, so carry the stored values through
        // untouched rather than inventing new ones.
        const payload: LoyaltyClubUpdatePayload = {
            name: club.name,
            isActive: formik.values.isActive,
            startDate:
                convertLocalToUtcString(formik.values.startDate, timezone) ??
                club.startDate,
            registrationStartDate: club.registrationStartDate,
            registrationEndDate: club.registrationEndDate,
            endDate:
                convertLocalToUtcString(formik.values.endDate, timezone) ??
                club.endDate,
            graceEndDate: convertLocalToUtcString(
                formik.values.graceEndDate,
                timezone
            ),
            timezone,
            translations: formik.values.translations.map((translation) => ({
                language: translation.language,
                name: translation.name,
                subtitle: translation.subtitle,
                description: translation.description,
                image: translation.image,
                ckImage: translation.ckImage,
                backgroundColor: translation.backgroundColor,
            })),
        };

        setIsSaving(true);
        try {
            await loyaltyClubStore.updateLoyaltyClub(payload, club.id);
            // Only when the ladder actually changed: it rewrites every tier and
            // option row, so re-sending an untouched ladder is needless churn.
            if (milestonesDirty) {
                await loyaltyClubStore.replaceTierLadder(
                    club.id,
                    toLadderPayload(milestones, groupsByKey)
                );
            }
            const refreshed = await loyaltyClubStore.fetchLoyaltyClub(club.id);
            setClub(refreshed);
            formik.resetForm({ values: toFormValues(refreshed) });
            setMilestones(toMilestoneForms(refreshed, couponGroups));
            setMilestonesDirty(false);
            NotificationManager.showSuccess('Configuration saved');
            return true;
        } catch {
            // The store already surfaced the error notification.
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty || milestonesDirty,
        // The provider reads a resolved promise as saved and navigates away, so
        // a refusal has to reject or the modal discards the very edits it
        // offered to save.
        onSave: async () => {
            if (!(await saveConfiguration())) {
                throw new Error('Configuration was not saved');
            }
        },
    });

    // Only on a save that happened: clearing the guard writes a ref the arming
    // effect will not rewrite while `dirty` stays true, so clearing it after a
    // refusal leaves the page unguarded for the rest of the session.
    const onSaveClick = async () => {
        if (await saveConfiguration()) {
            clearDirty();
        }
    };

    // Club and coupons together, not in two effects: folding a saved ladder back
    // into picker rows needs the catalogue those rows are grouped from, so a club
    // that arrived first would collapse against an empty catalogue and read as a
    // ladder with no coupons on it.
    React.useEffect(() => {
        let cancelled = false;

        setIsFetching(true);
        Promise.all([
            loyaltyClubStore.fetchRoadToRewardsClub(),
            loyaltyClubStore.fetchRewardOptionCoupons(),
        ])
            .then(([fetchedClub, fetchedCoupons]) => {
                if (cancelled) return;
                setCoupons(fetchedCoupons);
                setClub(fetchedClub);
                if (fetchedClub) {
                    formik.resetForm({ values: toFormValues(fetchedClub) });
                    setMilestones(
                        toMilestoneForms(
                            fetchedClub,
                            buildCouponGroups(fetchedCoupons)
                        )
                    );
                    setMilestonesDirty(false);
                }
            })
            .catch(() => {
                if (!cancelled) setClub(null);
            })
            .finally(() => {
                if (!cancelled) setIsFetching(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loyaltyClubStore]);

    const setDate = (field: keyof ConfigFormValues) => (date: unknown) => {
        const moment = date as { format: (f: string) => string } | null;
        formik.setFieldValue(
            field,
            moment ? moment.format(DATE_TIME_FORMAT) : ''
        );
    };

    return (
        <>
            <PageTopBar title="Road to rewards - Configuration">
                <SaveButton
                    onClick={onSaveClick}
                    loading={isSaving}
                    disabled={!club || isFetching}
                />
            </PageTopBar>

            {isFetching ? (
                <div className={style.loader}>
                    <Spin size="large" />
                </div>
            ) : !club ? (
                <div className="content_with_margins">
                    <Section title="Config">
                        <p>
                            No Road to Rewards campaign exists in this
                            environment. It is created by the campaign import,
                            not from the CMS.
                        </p>
                    </Section>
                </div>
            ) : (
                <div className="content_with_margins">
                    <div className={style.page_content}>
                        <div className={style.left_side}>
                            <Section title="Config">
                                <div className={style.config_grid}>
                                    <LabeledField label="App target">
                                        <Select
                                            value={formik.values.appTarget}
                                            disabled
                                            style={{ width: '100%' }}
                                            options={APP_TARGETS.map(
                                                (target) => ({
                                                    value: target,
                                                    label:
                                                        APP_TARGET_CONFIG[
                                                            target
                                                        ]?.label ?? target,
                                                })
                                            )}
                                        />
                                    </LabeledField>
                                    <LabeledField label="Timezone">
                                        <TimezoneSelect
                                            value={formik.values.timezone}
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                    'timezone',
                                                    value
                                                )
                                            }
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label="Start date"
                                        helperText="The moment members start accumulating visits."
                                    >
                                        <DatePicker
                                            allowClear={false}
                                            showTime
                                            format={DATE_TIME_FORMAT}
                                            value={
                                                formik.values.startDate || null
                                            }
                                            onChange={setDate('startDate')}
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label="End date"
                                        helperText="The moment visits stop counting."
                                    >
                                        <DatePicker
                                            allowClear={false}
                                            showTime
                                            format={DATE_TIME_FORMAT}
                                            value={
                                                formik.values.endDate || null
                                            }
                                            onChange={setDate('endDate')}
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label="Grace period date"
                                        helperText="Last moment a member can still claim a reward. Must not precede the end date."
                                        className={
                                            style.config_grid_second_column
                                        }
                                    >
                                        <DatePicker
                                            allowClear
                                            showTime
                                            format={DATE_TIME_FORMAT}
                                            value={
                                                formik.values.graceEndDate ||
                                                null
                                            }
                                            onChange={setDate('graceEndDate')}
                                        />
                                    </LabeledField>
                                </div>
                            </Section>

                            <Section title="Content">
                                <div className="columns_layout">
                                    {formik.values.translations.map(
                                        (translation, index) => (
                                            <div
                                                className="column"
                                                key={translation.language}
                                            >
                                                <div className="h5_bold">
                                                    {translation.language ===
                                                    Lang[0]
                                                        ? 'English'
                                                        : 'French'}
                                                </div>
                                                {/* One picker per brand, in the
                                                order and wording the ticket
                                                specifies. Note the fields below
                                                still carry an (EN)/(FR) suffix;
                                                these two follow the ticket. */}
                                                <div
                                                    className={
                                                        style.image_uploaders
                                                    }
                                                >
                                                    <LabeledField label="Featured image CK">
                                                        <ImageInput
                                                            url={
                                                                translation.ckImage
                                                            }
                                                            onChange={(url) =>
                                                                formik.setFieldValue(
                                                                    `translations[${index}].ckImage`,
                                                                    url
                                                                )
                                                            }
                                                            onDelete={() =>
                                                                formik.setFieldValue(
                                                                    `translations[${index}].ckImage`,
                                                                    null
                                                                )
                                                            }
                                                        />
                                                    </LabeledField>
                                                    <LabeledField label="Featured image CT">
                                                        <ImageInput
                                                            url={
                                                                translation.image
                                                            }
                                                            onChange={(url) =>
                                                                formik.setFieldValue(
                                                                    `translations[${index}].image`,
                                                                    url
                                                                )
                                                            }
                                                            onDelete={() =>
                                                                formik.setFieldValue(
                                                                    `translations[${index}].image`,
                                                                    null
                                                                )
                                                            }
                                                        />
                                                    </LabeledField>
                                                </div>
                                                <BackgroundColorPicker
                                                    value={
                                                        translation.backgroundColor
                                                    }
                                                    language={
                                                        translation.language
                                                    }
                                                    onChange={(value) =>
                                                        formik.setFieldValue(
                                                            `translations[${index}].backgroundColor`,
                                                            value
                                                        )
                                                    }
                                                />
                                                <LabeledField
                                                    label={`Title (${translation.language.toUpperCase()})`}
                                                >
                                                    <Input
                                                        name={`translations[${index}].name`}
                                                        value={translation.name}
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                </LabeledField>
                                            </div>
                                        )
                                    )}
                                </div>
                            </Section>
                            <MilestonesSection
                                milestones={milestones}
                                onChange={onMilestonesChange}
                                groups={couponGroups}
                                errors={milestoneErrors}
                            />
                        </div>

                        <div className={style.right_side}>
                            <DeepLinkSection
                                links={[
                                    {
                                        brand: Brand.CoucheTard,
                                        url: club.deepLink ?? null,
                                    },
                                    {
                                        brand: Brand.CircleK,
                                        url: club.ckDeepLink ?? null,
                                    },
                                ]}
                            />
                            <Section title="Status">
                                <div className={style.status_row}>
                                    <label className="footnote_description_bold">
                                        Active
                                    </label>
                                    <Switch
                                        checked={formik.values.isActive}
                                        onChange={(checked) =>
                                            formik.setFieldValue(
                                                'isActive',
                                                checked
                                            )
                                        }
                                    />
                                </div>
                            </Section>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});
